import { Controller } from '@hotwired/stimulus'
import mapboxgl from 'mapbox-gl'

const LINE_STYLES = {
  Red: ['#BA0C2F', '#fff'],
  Blue: ['#009CDE', '#fff'],
  Orange: ['#ED8B00', '#fff'],
  Silver: ['#919D9D', '#000'],
  Yellow: ['#FFD100', '#000'],
  Green: ['#00B140', '#fff']
}

export default class extends Controller {
  static targets = ['map', 'panel', 'totalStations', 'activeStations', 'issueStations', 'totalSpaces', 'availability', 'asOf']

  static values = {
    stationsUrl: String,
    payload: Object
  }

  connect () {
    this.activeStationId = null
    this.markers = []
    this.renderSummary()
    this.initMap()
    this.startRefresh()
  }

  disconnect () {
    if (this.refreshTimer) clearInterval(this.refreshTimer)
    this.markers.forEach((marker) => marker.remove())
    if (this.map) this.map.remove()
  }

  initMap () {
    const token = window.mapbox_data?.access_token
    if (!token || !this.hasMapTarget) return

    mapboxgl.accessToken = token
    this.map = new mapboxgl.Map({
      container: this.mapTarget,
      style: 'mapbox://styles/mapbox/streets-v9',
      center: [-77.0369, 38.9072],
      zoom: 11
    })
    this.map.addControl(new mapboxgl.NavigationControl(), 'bottom-right')
    this.map.on('load', () => this.renderMarkers(true))
  }

  startRefresh () {
    if (this.refreshTimer || !this.stationsUrlValue) return
    this.refreshTimer = setInterval(() => this.refresh(), 30 * 60 * 1000)
  }

  refresh () {
    fetch(this.stationsUrlValue, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then((res) => (res.ok ? res.json() : Promise.reject(res.status)))
      .then((payload) => {
        this.payloadValue = payload
        this.renderSummary()
        this.renderMarkers(false)
        if (this.activeStationId) this.openStation(this.activeStationId, { focusMap: false })
      })
      .catch(() => {})
  }

  stations () {
    return this.payloadValue?.stations || []
  }

  renderSummary () {
    const stations = this.stations()
    const active = stations.filter((station) => station.status === 'green' && !station.high_unknown).length
    const spaces = stations.reduce((sum, station) => sum + (station.total_spaces || 0), 0)
    const available = stations.reduce((sum, station) => sum + (station.availability?.available || 0), 0)
    const totals = stations.reduce((sum, station) => sum + (station.availability?.total || 0), 0)
    const percent = totals === 0 ? 0 : Math.round((available / totals) * 100)
    const stamps = stations.map((station) => station.availability_updated_at).filter(Boolean).sort()
    this.setText('totalStations', stations.length)
    this.setText('activeStations', active)
    this.setText('issueStations', stations.length - active)
    this.setText('totalSpaces', spaces)
    this.setText('availability', `${percent}%`)
    this.setText('asOf', `BikeLink assets · As of ${stamps.length ? stamps[stamps.length - 1] : 'unknown'}`)
  }

  setText (name, value) {
    const checker = `has${name.charAt(0).toUpperCase()}${name.slice(1)}Target`
    if (typeof this[checker] !== 'function' || !this[checker]()) return
    this[`${name}Target`].textContent = value
  }

  renderMarkers (fitMap) {
    if (!this.map) return
    this.markers.forEach((marker) => marker.remove())
    this.markers = []
    const bounds = new mapboxgl.LngLatBounds()
    this.stations().forEach((station) => {
      if (station.lat == null || station.lng == null) return
      const el = document.createElement('button')
      el.type = 'button'
      el.className = 'wmata-map-marker'
      el.style.cssText = `width:18px;height:18px;border-radius:9999px;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.35);cursor:pointer;background:${this.statusColor(station.status)};`
      el.title = station.name
      el.addEventListener('click', () => this.openStation(station.id, { focusMap: true }))
      const marker = new mapboxgl.Marker({ element: el }).setLngLat([station.lng, station.lat]).addTo(this.map)
      this.markers.push(marker)
      bounds.extend([station.lng, station.lat])
    })
    if (fitMap && !bounds.isEmpty()) this.map.fitBounds(bounds, { padding: 48, maxZoom: 13 })
  }

  statusColor (status) {
    if (status === 'green') return '#16a34a'
    if (status === 'red') return '#dc2626'
    return '#f59e0b'
  }

  openStation (stationId, { focusMap = true } = {}) {
    const station = this.stations().find((item) => String(item.id) === String(stationId))
    if (!station || !this.hasPanelTarget) return
    this.activeStationId = station.id
    this.panelTarget.replaceChildren(this.panelFragment(station))
    if (focusMap && this.map && station.lat != null && station.lng != null) {
      this.map.easeTo({ center: [station.lng, station.lat], zoom: Math.max(this.map.getZoom(), 13), duration: 450 })
    }
  }

  panelFragment (station) {
    const root = document.createElement('div')
    const header = document.createElement('div')
    header.className = 'flex items-start justify-between gap-3'
    header.appendChild(this.el('h4', `${station.name} Station`, 'mb-0'))
    const stateBadge = this.maintenanceBadge(station)
    if (stateBadge) header.appendChild(stateBadge)
    root.appendChild(header)
    root.appendChild(this.lineBadges(station))
    if (station.offline) {
      root.appendChild(this.badge(`Offline since ${station.availability_updated_at || 'unknown'}`, 'error-tag'))
    }
    if (station.address) root.appendChild(this.el('p', station.address, 'text-sm text-gray-400 mb-2'))
    const spaces = document.createElement('div')
    spaces.className = 'flex flex-wrap gap-4 text-sm'
    spaces.appendChild(this.el('span', `Total spaces ${station.total_spaces ?? '—'}`))
    spaces.appendChild(this.el('span', `Oversized ${station.total_oversized_spaces ?? '—'}`))
    root.appendChild(spaces)
    this.appendAvailability(root, station)
    this.appendUptime(root, station)
    this.appendSla(root, station)
    this.appendMaintenance(root, station)
    this.appendEquipment(root, station)
    this.appendStatic(root, station)
    root.appendChild(this.el('p', `Last availability update: ${station.availability_updated_at || 'unknown'}`, 'text-xs text-gray-400 mt-4'))
    root.appendChild(this.links(station))
    return root
  }

  appendAvailability (root, station) {
    const section = this.section('Live availability (BikeLink assets only)')
    const availability = station.availability || {}
    if (availability.total == null) {
      section.appendChild(this.el('p', 'no data', 'text-sm text-gray-400 mb-0'))
    } else {
      this.pills(section, [
        [`${availability.available || 0} avail`, 'success-tag'],
        [`${availability.unavailable || 0} unavail`, 'error-tag'],
        [`${availability.unknown || 0} unknown`, 'secondary-tag'],
        [`${availability.total} total`, 'secondary-tag']
      ])
    }
    if (station.high_unknown) {
      section.appendChild(this.el('p', 'High unknown count (possible connectivity issue)', 'text-sm text-warning-500 mt-2 mb-0'))
    }
    root.appendChild(section)
  }

  lineBadges (station) {
    const wrap = document.createElement('div')
    wrap.className = 'mb-2'
    const lines = station.metro_lines || []
    if (!lines.length) {
      wrap.appendChild(this.el('span', 'Metro line: unknown', 'text-sm text-gray-400'))
      return wrap
    }
    lines.forEach((line) => {
      const badge = this.el('span', line, 'inline-block text-xs px-2 py-0.5 rounded mr-1 mb-1')
      const colors = LINE_STYLES[line] || ['#666', '#fff']
      badge.style.background = colors[0]
      badge.style.color = colors[1]
      wrap.appendChild(badge)
    })
    return wrap
  }

  appendUptime (root, station) {
    if (station.uptime_30d == null && station.uptime_90d == null) return
    const section = this.section('Uptime')
    section.appendChild(this.el('p', `30 days: ${this.percent(station.uptime_30d)} · 90 days: ${this.percent(station.uptime_90d)}`, 'text-sm mb-0'))
    const threshold = this.payloadValue?.uptime_threshold
    if (threshold != null && station.uptime_30d != null && station.uptime_30d < threshold) {
      section.appendChild(this.badge(`Below ${threshold}% target`, 'warning-tag mt-2'))
    }
    root.appendChild(section)
  }

  appendSla (root, station) {
    const sla = station.sla
    if (!sla) return
    const issues = sla.issues || []
    const counted = (sla.within || 0) + (sla.at_risk || 0) + (sla.breaching || 0)
    if (counted === 0 && issues.length === 0) return
    const section = this.section('SLA compliance (work orders)')
    this.pills(section, [
      [`${sla.within || 0} within`, 'success-tag'],
      [`${sla.at_risk || 0} at risk`, 'warning-tag'],
      [`${sla.breaching || 0} breaching`, 'error-tag']
    ])
    issues.forEach((issue) => {
      const row = document.createElement('p')
      row.className = 'text-sm mt-2 mb-0'
      const label = issue.state === 'breaching' ? 'BREACH' : 'At risk'
      row.appendChild(this.badge(label, issue.state === 'breaching' ? 'error-tag' : 'warning-tag'))
      const type = issue.asset_type ? ` · ${issue.asset_type}` : ''
      row.appendChild(document.createTextNode(` ${issue.priority || ''}${type} · ${issue.hours_elapsed}h / ${issue.hours_allowed}h · #${issue.id} ${issue.description}`))
      section.appendChild(row)
    })
    root.appendChild(section)
  }

  appendMaintenance (root, station) {
    const maintenance = station.maintenance || {}
    if (maintenance.threshold_days == null) return
    const section = this.section(`Maintenance (every ${maintenance.threshold_days} days)`)
    const row = document.createElement('div')
    row.className = 'flex flex-wrap items-center gap-2'
    const last = maintenance.last_serviced_at
      ? `Last serviced: ${maintenance.last_serviced_at} (${maintenance.days_since} days ago)`
      : 'no completed maintenance on record'
    row.appendChild(this.el('span', last, 'text-sm'))
    const badge = this.maintenanceBadge(station)
    if (badge) row.appendChild(badge)
    section.appendChild(row)
    root.appendChild(section)
  }

  maintenanceBadge (station) {
    const maintenance = station.maintenance || {}
    const labels = { within: ['On schedule', 'success-tag'], at_risk: ['Due soon', 'warning-tag'], overdue: ['Overdue', 'error-tag'], never: ['Never serviced', 'warning-tag'] }
    const entry = labels[maintenance.state]
    if (!entry) return null
    return this.badge(entry[0], entry[1])
  }

  appendEquipment (root, station) {
    const section = this.section('Connected equipment')
    ;(station.assets || []).forEach((group) => {
      const title = group.type.endsWith('s') ? group.type : `${group.type}s`
      const head = document.createElement('div')
      head.className = 'flex items-center justify-between gap-3 mt-3 text-sm font-medium'
      head.appendChild(this.el('span', title))
      const complete = Number(group.available) === Number(group.total) && Number(group.total) > 0
      head.appendChild(this.el('span', `${group.available}/${group.total} available`, complete ? 'text-success-500' : 'text-warning-500'))
      section.appendChild(head)
      ;(group.facilities || []).forEach((facility) => {
        const row = document.createElement('div')
        row.className = 'mt-2 ml-1'
        const name = document.createElement('div')
        name.className = 'flex items-center gap-2 text-sm'
        const dot = document.createElement('span')
        const online = facility.online === true ? 'bg-success' : (facility.online === false ? 'bg-error' : 'bg-gray-300')
        dot.className = `inline-block w-2.5 h-2.5 rounded-full ${online}`
        name.appendChild(dot)
        name.appendChild(document.createTextNode(facility.name))
        row.appendChild(name)
        this.countPills(row, facility)
        section.appendChild(row)
      })
    })
    root.appendChild(section)
  }

  countPills (parent, facility) {
    if (facility.total == null) {
      parent.appendChild(this.el('p', 'no data', 'text-xs text-gray-400 mb-0 mt-1'))
      return
    }
    if (!facility.oversized_total) {
      this.pills(parent, this.countItems(facility))
      return
    }
    const standard = {
      available: this.subtract(facility.available, facility.oversized_available),
      unavailable: this.subtract(facility.unavailable, facility.oversized_unavailable),
      unknown: this.subtract(facility.unknown, facility.oversized_unknown),
      total: this.subtract(facility.total, facility.oversized_total)
    }
    parent.appendChild(this.el('p', 'Standard', 'text-xs text-gray-400 mb-0 mt-1'))
    this.pills(parent, this.countItems(standard))
    parent.appendChild(this.el('p', 'Oversized', 'text-xs text-gray-400 mb-0 mt-1'))
    this.pills(parent, this.countItems({
      available: facility.oversized_available ?? 0,
      unavailable: facility.oversized_unavailable ?? 0,
      unknown: facility.oversized_unknown ?? 0,
      total: facility.oversized_total
    }))
  }

  countItems (counts) {
    return [
      [`${counts.available ?? 0} avail`, 'success-tag'],
      [`${counts.unavailable ?? 0} unavail`, 'error-tag'],
      [`${counts.unknown ?? 0} unknown`, 'secondary-tag'],
      [`${counts.total} total`, 'secondary-tag']
    ]
  }

  appendStatic (root, station) {
    const section = this.section('Static assets (from Sitetracker)')
    const assets = station.static_assets || []
    if (!assets.length) {
      section.appendChild(this.el('p', 'None synced from Sitetracker yet', 'text-sm text-gray-400 mb-0'))
      root.appendChild(section)
      return
    }
    const labels = { ok: ['In service', 'success-tag'], attention: ['Needs attention', 'warning-tag'], down: ['Out of service', 'error-tag'] }
    assets.forEach((asset) => {
      const row = document.createElement('div')
      row.className = 'flex flex-wrap items-center gap-2 mt-2 text-sm'
      const serial = asset.serial ? ` s/n ${asset.serial}` : ''
      const installed = asset.installed_on ? ` installed ${asset.installed_on}` : ''
      row.appendChild(this.el('span', `${asset.name} · ${asset.asset_type} · ${asset.catalogue_item || ''}${serial}${installed}`))
      const health = labels[asset.health] || [asset.health, 'secondary-tag']
      row.appendChild(this.badge(health[0], health[1]))
      section.appendChild(row)
    })
    root.appendChild(section)
  }

  links (station) {
    const wrap = document.createElement('div')
    wrap.className = 'mt-4 flex flex-wrap gap-2'
    wrap.appendChild(this.anchor('Work orders', `/wmata/work_orders?station_key=${encodeURIComponent(station.station_key)}`))
    wrap.appendChild(this.anchor('Inspections', `/wmata/inspections?station_key=${encodeURIComponent(station.station_key)}`))
    wrap.appendChild(this.anchor('Maintenance history', `/wmata/uptime?station_key=${encodeURIComponent(station.station_key)}`))
    if (station.sitetracker_url) wrap.appendChild(this.anchor('Open in Sitetracker', station.sitetracker_url, true))
    const facilities = []
    ;(station.assets || []).forEach((group) => {
      ;(group.facilities || []).forEach((facility) => {
        if (facility.external_id) facilities.push(facility)
      })
    })
    if (facilities.length) {
      const block = document.createElement('div')
      block.className = 'w-full mt-2'
      block.appendChild(this.el('h5', 'BikeLink management', 'mb-2 text-sm font-semibold'))
      const row = document.createElement('div')
      row.className = 'flex flex-wrap gap-2'
      facilities.forEach((facility) => {
        row.appendChild(this.anchor(facility.name, `https://www.bikelink.org/owners/locations/${facility.external_id}/lockers`, true))
      })
      block.appendChild(row)
      wrap.appendChild(block)
    }
    return wrap
  }

  percent (value) {
    return value == null ? 'n/a' : `${value}%`
  }

  subtract (total, part) {
    if (total == null) return '—'
    return Number(total) - Number(part || 0)
  }

  section (title) {
    const node = document.createElement('section')
    node.className = 'mt-4 pt-3 border-t border-gray-200'
    node.appendChild(this.el('h5', title, 'mb-2 text-sm font-semibold'))
    return node
  }

  pills (parent, items) {
    const row = document.createElement('div')
    row.className = 'flex flex-wrap gap-1'
    items.forEach(([text, tone]) => row.appendChild(this.badge(text, tone)))
    parent.appendChild(row)
  }

  el (tag, text, className) {
    const node = document.createElement(tag)
    if (className) node.className = className
    node.textContent = text
    return node
  }

  badge (text, className) {
    return this.el('span', text, `tag ${className}`)
  }

  anchor (text, href, blank) {
    const link = document.createElement('a')
    link.href = href
    link.textContent = text
    link.className = 'btn btn-secondary btn-sm'
    if (blank) {
      link.target = '_blank'
      link.rel = 'noopener'
    }
    return link
  }
}
